// apps/landing-page/src/lib/search.ts
// One matcher for both search surfaces (navbar dialog and the /tests page), so a
// query ranks the same everywhere.

export interface SearchDoc {
  title: string;
  /** Description or excerpt. */
  text?: string | null;
  /** Exam family names, e.g. "UTBK / SNBT". */
  families?: string[];
}

/** Lowercase and strip diacritics, so "Pengantár" and "pengantar" are the same word. */
export function normalize(value: string): string {
  return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

/**
 * Keeps the items where every query word appears in the title, text or a family name,
 * best match first: title starts with the first word, then title contains every word,
 * then anything that needed the description or family. Ties keep their input order
 * (Array.prototype.sort is stable). An empty query keeps everything as is.
 */
export function searchItems<T>(items: readonly T[], query: string, toDoc: (item: T) => SearchDoc): T[] {
  const words = normalize(query).split(/\s+/).filter(Boolean);
  if (words.length === 0) return [...items];

  const ranked: { item: T; tier: number }[] = [];
  for (const item of items) {
    const doc = toDoc(item);
    const title = normalize(doc.title);
    const rest = normalize([doc.text ?? '', ...(doc.families ?? [])].join(' '));
    if (!words.every((w) => title.includes(w) || rest.includes(w))) continue;

    const allInTitle = words.every((w) => title.includes(w));
    ranked.push({ item, tier: allInTitle ? (title.startsWith(words[0]) ? 0 : 1) : 2 });
  }
  return ranked.sort((a, b) => a.tier - b.tier).map((r) => r.item);
}
